/**
 * Canonical token values for the AxisNode Presentation Platform.
 *
 * Source of truth: docs/architecture/platform/design-tokens-input.md
 * (OE-PP-001 §1.3 / §1.9 item 3). Values must match that document exactly —
 * this file is a mechanical transcription, not an interpretation.
 */

export const color = {
  primary: "#0F172A",
  secondary: "#334155",
  accent: "#0369A1",
  background: "#F8FAFC",
  text: "#020617",
  "text-muted": "#475569",
  success: "#22C55E",
  warning: "#F59E0B",
  danger: "#DC2626",
  "ai-marker": "#6366F1",
} as const;

export const typography = {
  fontFamily: {
    sans: '"Plus Jakarta Sans", ui-sans-serif, system-ui, sans-serif',
    heading: '"Plus Jakarta Sans", ui-sans-serif, system-ui, sans-serif',
    body: '"Plus Jakarta Sans", ui-sans-serif, system-ui, sans-serif',
  },
  /** Exact Google Fonts URL from design-tokens-input.md §2. */
  googleFontsUrl:
    "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700&display=swap",
  weights: [300, 400, 500, 600, 700] as const,
  size: {
    xs: "12px",
    sm: "14px",
    base: "16px",
    lg: "20px",
    xl: "24px",
    "2xl": "32px",
    "3xl": "40px",
  },
  /** Body line-height range from design-tokens-input.md §2 (1.5–1.75). */
  lineHeight: {
    body: 1.5,
    bodyLoose: 1.75,
  },
} as const;

export const space = {
  1: "4px",
  2: "8px",
  3: "12px",
  4: "16px",
  6: "24px",
  8: "32px",
  12: "48px",
  16: "64px",
} as const;

export const elevation = {
  0: "none",
  1: "0 1px 3px rgba(0,0,0,0.1)",
  2: "0 4px 6px rgba(0,0,0,0.1)",
  3: "0 10px 20px rgba(0,0,0,0.1)",
  4: "0 20px 40px rgba(0,0,0,0.15)",
} as const;

export const motion = {
  duration: {
    fast: "150ms",
    base: "200ms",
    slow: "300ms",
  },
  easing: {
    enter: "ease-out",
    exit: "ease-in",
  },
} as const;

export const breakpoint = {
  sm: "375px",
  md: "768px",
  lg: "1024px",
  xl: "1440px",
} as const;

export const size = {
  "table-row-height": "36px",
  "sidebar-width": "240px",
  "header-height": "56px",
} as const;

export const z = {
  base: 10,
  dropdown: 20,
  modal: 30,
  toast: 50,
} as const;

/** Nested token tree — path keys mirror design-tokens-input.md token names. */
export const tokens = {
  color,
  type: {
    family: typography.fontFamily,
    googleFontsUrl: typography.googleFontsUrl,
    weights: typography.weights,
    xs: typography.size.xs,
    sm: typography.size.sm,
    base: typography.size.base,
    lg: typography.size.lg,
    xl: typography.size.xl,
    "2xl": typography.size["2xl"],
    "3xl": typography.size["3xl"],
    lineHeight: typography.lineHeight,
  },
  space,
  elevation,
  motion,
  breakpoint,
  size,
  z,
} as const;

export type Tokens = typeof tokens;
